/**
 * Composition root of the media layer: the one place that decides which implementations are
 * used. To change course (e.g. add OPFS or cloud storage, or server-side processing), register
 * another provider / pass another analyzer, generator or cache here.
 */
import { createBrowserAnalyzer, createBrowserDerivatives, createOpfsCache } from './derivatives/index.ts';
import { LOCAL_FOLDER_PROVIDER_ID, LocalFolderProvider } from './providers/local-folder.ts';
import { ProviderRegistry } from './registry.ts';
import { MediaService } from './service.ts';

export const localFolder = new LocalFolderProvider();

const registry = new ProviderRegistry(LOCAL_FOLDER_PROVIDER_ID).register(localFolder);

export const mediaService = new MediaService({
  registry,
  analyzer: createBrowserAnalyzer(),
  derivatives: createBrowserDerivatives(),
  cache: createOpfsCache(),
});

/** Whether this browser can run the editor's media layer at all. */
export function mediaSupport(): { supported: boolean; reason?: string } {
  const provider = registry.importProvider;
  return provider.isSupported()
    ? { supported: true }
    : { supported: false, reason: `${provider.label} storage is not available in this browser.` };
}

// Development aid: inspect media state from the browser console (`__cutroomMedia.state()`).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  void import('./service.ts').then(({ useMediaRuntime }) => {
    Object.assign(window, {
      __cutroomMedia: { service: mediaService, state: () => useMediaRuntime.getState() },
    });
  });
}
